import { DashedTag } from '@praetorian-chariot/ui';
import { SoundRecognitionSearch as SoundRecognitionSearchIcon } from '@praetorian-chariot/ui';
import { useMemo } from 'react';

import {
  getColumnWidth,
  isColumnSortable,
} from '@/components/table/columns/registry';
import { Table } from '@/components/table/Table';
import { Column } from '@/components/table/types';
import {
  parseTechnologyProductName,
  parseTechnologyVersion,
} from '@/sections/technology/utils';
import { useGlobalState } from '@/state/global.state';
import { Port, Technology } from '@/types';

export interface TechnologyAttributeUnion {
  technology?: Technology;
  port: Port;
  visited: string;
}

export function TechnologiesTab({
  technologies,
}: {
  technologies: TechnologyAttributeUnion[];
}) {
  const { drawerState } = useGlobalState();

  const columns = useMemo<Column<TechnologyAttributeUnion>[]>(
    () => [
      {
        id: '',
        label: 'Port',
        copy: true,
        cell: data => data.port.port.toString(),
        ...getColumnWidth('shared', 'port'),
        sorting: isColumnSortable('shared', 'port'),
      },
      {
        id: '',
        label: 'Protocol',
        copy: true,
        cell: data => (
          <DashedTag
            className="max-w-full justify-normal space-x-0 truncate overflow-hidden text-center [&>span]:w-full"
            label={data.port.protocol}
            size="xs"
          />
        ),
        ...getColumnWidth('shared', 'protocol'),
        sorting: isColumnSortable('shared', 'protocol'),
      },
      {
        id: '',
        label: 'Service',
        copy: true,
        cell: data => data.port.service || '-',
        ...getColumnWidth('shared', 'service'),
        sorting: isColumnSortable('shared', 'service'),
      },
      {
        id: '',
        label: 'Vendor',
        copy: true,
        cell: data => data.technology?.vendor || '-',
        ...getColumnWidth('technology', 'vendor'),
        sorting: isColumnSortable('technology', 'vendor'),
      },
      {
        id: '',
        label: 'Product',
        copy: true,
        cell: data =>
          data.technology?.product
            ? parseTechnologyProductName(data.technology.product)
            : '-',
        ...getColumnWidth('technology', 'product'),
        sorting: isColumnSortable('technology', 'product'),
      },
      {
        id: '',
        label: 'Version',
        copy: true,
        cell: data =>
          data.technology?.version
            ? parseTechnologyVersion(data.technology.version)
            : '-',
        ...getColumnWidth('technology', 'version'),
        sorting: isColumnSortable('technology', 'version'),
      },
      {
        id: '',
        label: 'CPE String',
        copy: true,
        cell: data => data.technology?.cpe || '-',
        ...getColumnWidth('technology', 'cpeString'),
        sorting: isColumnSortable('technology', 'cpeString'),
      },
      {
        id: 'visited',
        label: 'Last Seen',
        copy: true,
        cell: 'date',
        ...getColumnWidth('shared', 'visited'),
        sorting: isColumnSortable('shared', 'visited'),
      },
    ],
    []
  );

  return (
    <div className="flex size-full flex-col overflow-hidden">
      <div className="flex-1 overflow-auto" style={{ minWidth: 0 }}>
        <Table
          tableClassName="[&_tr>th:first-child]:pl-6 [&_tr>td:first-child]:pl-6 [&_tr>td]:py-4"
          name={'assetDrawerTechnologies'}
          columns={columns}
          data={technologies}
          onRowClick={data => {
            if (data.technology) {
              drawerState.handleOpenTechnologyDrawer(data.technology);
            }
          }}
          noData={{
            icon: (
              <SoundRecognitionSearchIcon className="text-chariot-stroke text-[100px]" />
            ),
            title: 'No Technology Information Collected',
          }}
          selection={{ hide: true }}
          status={'success'}
          error={null}
        />
      </div>
    </div>
  );
}
